import React, { useState, useEffect, useCallback } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import GlassCard from '../components/ui/GlassCard';
import ScrollReveal from '../components/ui/ScrollReveal';
import PageHeader from '../components/PageHeader';
import {
  stockPortfolioService,
  StockSimulatedPosition,
  StockSimulatedAccount,
} from '../services/stockPortfolioService';
import { useToastStore } from '../stores/useToastStore';

const StockPortfolio: React.FC = () => {
  const setCurrentPage = useSetCurrentPage();
  const showToast = useToastStore((state) => state.show);

  const [account, setAccount] = useState<StockSimulatedAccount | null>(null);
  const [positions, setPositions] = useState<StockSimulatedPosition[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'OPEN' | 'CLOSED'>('OPEN');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [accRes, posRes] = await Promise.all([
        stockPortfolioService.getAccount(),
        stockPortfolioService.getPositions(activeTab),
      ]);

      setAccount(accRes);
      setPositions(posRes);
    } catch (error: any) {
      showToast(error.message || '获取数据失败', 'error');
    } finally {
      setLoading(false);
    }
  }, [activeTab, showToast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleClosePosition = async (id: number) => {
    const exitPriceStr = prompt('请输入平仓价格:');
    if (!exitPriceStr) return;

    const exitPrice = parseFloat(exitPriceStr);
    if (isNaN(exitPrice)) {
      showToast('请输入有效的价格', 'error');
      return;
    }

    try {
      await stockPortfolioService.closePosition(id, exitPrice);
      showToast('平仓成功', 'success');
      fetchData();
    } catch (error: any) {
      showToast(error.message || '平仓失败', 'error');
    }
  };

  const handleUpdatePnL = async () => {
    try {
      showToast('正在更新实时盈亏...', 'info');
      await stockPortfolioService.updateLivePnL();
      fetchData();
      showToast('更新完成', 'success');
    } catch (error: any) {
      showToast(error.message || '更新失败', 'error');
    }
  };

  return (
    <GradientBackground className="pb-20">
      <ParticleBackground />

      <PageHeader
        title="模拟持仓"
        onBack={() => setCurrentPage('stock-dashboard')}
        rightContent={
          <button
            onClick={handleUpdatePnL}
            className="flex items-center justify-center w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 transition-all duration-300"
            title="刷新实时盈亏"
          >
            <span>🔄</span>
          </button>
        }
      />

      <div className="pt-16 px-4 space-y-6 relative z-10">
        {/* 账户资产概览 */}
        {account && (
          <ScrollReveal animation="fadeInDown">
            <GlassCard className="p-5 border-white/20 bg-gradient-to-br from-white/10 to-white/5">
              <div className="flex justify-between items-center mb-4">
                <span className="text-white/40 text-xs font-medium">总资产 (CNY)</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                    account.pnlPercent >= 0 ? 'bg-red-500/20 text-red-400' : 'bg-green-500/20 text-green-400'
                  }`}
                >
                  {account.pnlPercent >= 0 ? '+' : ''}
                  {(account.pnlPercent * 100).toFixed(2)}%
                </span>
              </div>
              <div className="text-3xl font-bold text-white mb-6 font-mono tracking-tight">
                ¥{account.totalEquity.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <span className="text-white/30 text-[10px] uppercase tracking-wider block">可用现金</span>
                  <span className="text-white/90 text-sm font-semibold font-mono">
                    ¥{account.availableCash.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
                <div className="space-y-1 text-right">
                  <span className="text-white/30 text-[10px] uppercase tracking-wider block">累计盈亏</span>
                  <span
                    className={`text-sm font-semibold font-mono ${
                      account.cumulativePnl >= 0 ? 'text-red-400' : 'text-green-400'
                    }`}
                  >
                    {account.cumulativePnl >= 0 ? '+' : ''}
                    {account.cumulativePnl.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </GlassCard>
          </ScrollReveal>
        )}

        {/* Tab 切换 */}
        <div className="flex p-1 bg-white/5 backdrop-blur-md rounded-xl border border-white/10">
          <button
            onClick={() => setActiveTab('OPEN')}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'OPEN'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                : 'text-white/40 hover:text-white/60'
            }`}
          >
            当前持仓
          </button>
          <button
            onClick={() => setActiveTab('CLOSED')}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-all ${
              activeTab === 'CLOSED'
                ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/25'
                : 'text-white/40 hover:text-white/60'
            }`}
          >
            历史平仓
          </button>
        </div>

        {/* 持仓列表 */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : positions.length === 0 ? (
          <div className="py-20 text-center">
            <div className="text-4xl mb-4">📭</div>
            <p className="text-white/40 text-sm">暂无{activeTab === 'OPEN' ? '持仓' : '历史记录'}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {positions.map((pos, index) => (
              <ScrollReveal key={pos.id} animation="fadeInUp" delay={index * 50}>
                <GlassCard className="p-4 border-white/10">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-white font-bold">{pos.name}</h3>
                        <span className="text-white/30 text-[10px] font-mono">{pos.symbol}</span>
                      </div>
                      <p className="text-white/40 text-[10px] mt-0.5">
                        开仓时间: {new Date(pos.entryTime).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <div
                        className={`text-lg font-bold font-mono ${pos.pnlPercent && pos.pnlPercent >= 0 ? 'text-red-400' : 'text-green-400'}`}
                      >
                        {pos.pnlPercent != null ? (
                          <>
                            {pos.pnlPercent >= 0 ? '+' : ''}
                            {(pos.pnlPercent * 100).toFixed(2)}%
                          </>
                        ) : (
                          '--'
                        )}
                      </div>
                      <div className="text-[10px] text-white/40">收益率</div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 pt-3 border-t border-white/5">
                    <div>
                      <span className="text-white/30 text-[10px] block">开仓价格</span>
                      <span className="text-white/80 text-sm font-mono">¥{pos.entryPrice}</span>
                    </div>
                    {activeTab === 'OPEN' ? (
                      <div className="text-right">
                        <span className="text-white/30 text-[10px] block">当前价格</span>
                        <span className="text-white/80 text-sm font-mono">¥{pos.metadata?.currentPrice || '--'}</span>
                      </div>
                    ) : (
                      <div className="text-right">
                        <span className="text-white/30 text-[10px] block">平仓价格</span>
                        <span className="text-white/80 text-sm font-mono">¥{pos.exitPrice}</span>
                      </div>
                    )}
                  </div>

                  {activeTab === 'OPEN' && (
                    <div className="mt-4 flex gap-2">
                      <button
                        onClick={() => handleClosePosition(pos.id)}
                        className="flex-1 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 rounded-xl text-red-400 text-xs font-bold transition-all"
                      >
                        平仓
                      </button>
                    </div>
                  )}

                  {pos.metadata?.strategy && (
                    <div className="mt-3 flex gap-2">
                      <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-400 text-[10px] rounded border border-blue-500/20">
                        策略: {pos.metadata.strategy}
                      </span>
                      {pos.metadata.industry && (
                        <span className="px-1.5 py-0.5 bg-white/5 text-white/40 text-[10px] rounded border border-white/10">
                          {pos.metadata.industry}
                        </span>
                      )}
                    </div>
                  )}
                </GlassCard>
              </ScrollReveal>
            ))}
          </div>
        )}
      </div>
    </GradientBackground>
  );
};

export default StockPortfolio;
